// SafeyeNodeItemsEditButton
import {Button, Menu, MenuItem} from '@mui/material';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useState,
} from 'react';

import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {SafeyeNode} from '../../../interfaces/SafeyeNode';
import reduxActions from '../../../redux/actions';
import HistoryReportMenuItem from '../../common/HistoryReportMenuItem';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  item?: SafeyeNode;
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  onOpenItem?: Function;
  onOpenHistory?: Function;
  onToggleNoHeartbeatEvent: Function;
  onDone?: () => void;
}

const SafeyeNodeItemsEditButton = <T extends ComponentType = typeof Button>({
  item,
  component,
  componentProps,
  children,
  onOpenItem,
  onOpenHistory,
  onToggleNoHeartbeatEvent,
}: Props<T>) => {
  const Component = component ?? Button;
  const myPermissions = useAppSelector(({app}) => app.me?.permissions);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
  const menuOpen = !!menuAnchorEl;

  const reduxAppDispatch = useAppDispatch();
  const loadAssets = () => {
    reduxAppDispatch(reduxActions.assets.fetchSafeyeNodes);
  };
  return (
    <>
      <Component
        {...componentProps}
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        {children}
      </Component>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onBackdropClick={() => setMenuAnchorEl(undefined)}
      >
        <MenuItem
          onClick={() => {
            onOpenItem?.(item?.id, 'assign');
            setMenuAnchorEl(undefined);
          }}
        >
          {item?.asset_machine_id ? 'Reassign' : 'Assign to the machine'}
        </MenuItem>

        <MenuItem
          onClick={() => {
            onToggleNoHeartbeatEvent(item);
            setMenuAnchorEl(undefined);
          }}
        >
          {item?.disable_no_heartbeat_event ? 'Enable' : 'Disable'} No Heartbeat
          Event
        </MenuItem>

        {item?.asset_machine_id && [
          <HistoryReportMenuItem
            type="hazard_ai_detection_log"
            id={item?.id}
            onOpen={() => {
              loadAssets();
              onOpenHistory?.(item?.id, 'hazard_ai_detection_log');
            }}
            onAfterAction={() => setMenuAnchorEl(undefined)}
            key="hazard_ai_detection_log"
          >
            Open AI Detection Log
          </HistoryReportMenuItem>,
          <HistoryReportMenuItem
            type="machine_detections_by_camera"
            id={item?.id}
            onOpen={() => {
              loadAssets();
              onOpenHistory?.(item?.id, 'machine_detections_by_camera');
            }}
            onAfterAction={() => setMenuAnchorEl(undefined)}
            key="machine_detections_by_camera"
          >
            Open Detections by Camera
          </HistoryReportMenuItem>,
          myPermissions?.includes('get::/hazard-ai/detection-heat-map') && (
            <HistoryReportMenuItem
              type="hazard_ai_heatmap"
              id={item?.id}
              onOpen={() => {
                loadAssets();
                onOpenHistory?.(item?.id, 'hazard_ai_heatmap');
              }}
              onAfterAction={() => setMenuAnchorEl(undefined)}
              key="hazard_ai_heatmap"
            >
              Open Heatmap Panel
            </HistoryReportMenuItem>
          ),
          <HistoryReportMenuItem
            type="hazard_ai_machine_location"
            id={item?.asset_machine_id}
            onOpen={() => {
              loadAssets();
              onOpenHistory?.(
                item?.asset_machine_id,
                'hazard_ai_machine_location'
              );
            }}
            onAfterAction={() => setMenuAnchorEl(undefined)}
            key="hazard_ai_machine_location"
          >
            Open Location History Panel
          </HistoryReportMenuItem>,
        ]}
      </Menu>
    </>
  );
};

export default SafeyeNodeItemsEditButton;
